import React, {Fragment, useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useHistory, useParams} from 'react-router-dom'
import {Button, Col, OverlayTrigger, Popover, Row} from 'react-bootstrap';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

import {UPSERT_SAMPLE_EVENT} from "../../../Redux/Actions/Types/offlineDataActionTypes";
import {SET_AUTO_UPLOAD_ID} from "../../../Redux/Actions/Types/syncStatusTypes";

import {
    selectProcedures,
    selectProjectById,
    selectSampleEventById,
    selectSampleEventPhaseIdsByProjectId,
    selectSampleEventPhaseTypes,
    selectSampleEventProcedureIdsKeyedByPhaseIdForProjectId,
    selectUsers
} from "../../../Redux/Selectors/nodeSelectors";
import {HIERARCHY_TYPE_SAMPLE_EVENT} from "../../../Constants/hierarchy";

import {useContextTools, useContextToolsByProjectId} from '../../../Hooks/ToolHooks';
import {useNextTempId} from "../../../Hooks/TempIdHooks";
import {hasInvalidField, validateBetween, validateInteger} from "../../../Hooks/FormHooks";
import DateUtils from "../../../Utils/DateUtils";

import PageContent from '../../Layout/Page/PageContent';
import PageFooter from '../../Layout/Page/PageFooter';
import Title from '../../Layout/TitleComponent';
import PageContainer from '../../Layout/Page/PageContainer';
import FormInput from '../../Common/FormInput';
import CheckboxList from '../../Common/CheckboxList';
import PageHeader from "../../Layout/Page/PageHeader";
import RouteLeavingGuard from "../../Common/RouteLeavingGuard";

import {useCopyEventData} from "../../../Hooks/DataCopyHooks";
import {useBreadcrumbs} from "../../../Hooks/BreadcrumbHooks";
import {useUpdateEffect} from "../../../Hooks/EffectHooks";
import SaveButtonWithHelpIcon from "../../Common/SaveButtonWithHelpIcon";
import {useQueryParams} from "../../../Hooks/RouteHooks";
import {TEMP_ID_PREFIX} from "../../../Constants/misc";
import ConfirmSaveModal from "../../Common/ConfirmSaveModal";


const NewEventPage = (props) => {
    useContextTools([]);
    const history = useHistory();
    const params = useParams();
    const dispatch = useDispatch();
    const nextId = useNextTempId();
    const {projectId, autoUpload} = useQueryParams();

    const existingSampleEvent = useSelector(state => selectSampleEventById(state, params.eventId));
    const project = useSelector(state => selectProjectById(state, existingSampleEvent?.projectId ?? projectId));
    const users = useSelector(state => selectUsers(state, project.projectId));
    const procedures = useSelector(state => selectProcedures(state, project.parentHierarchyId));
    const sampleEventPhaseTypes = useSelector(state => selectSampleEventPhaseTypes(state, project.parentHierarchyId));
    const existingSampleEventPhaseIds = useSelector(state => selectSampleEventProcedureIdsKeyedByPhaseIdForProjectId(state, project.projectId));
	
	const copyEventData = useCopyEventData(project.projectId);
	
    const breadcrumbTitle = params.eventId ? 'Edit Monitoring Event' : 'New Monitoring Event';
    useBreadcrumbs(breadcrumbTitle, project.projectId);
    useContextToolsByProjectId(project.projectId);

    const [sampleEventId, setSampleEventId] = useState(null);
    const [hierarchyId, setHierarchyId] = useState(null);
    const [startDate, setStartDate] = useState(DateUtils.GetCurrentDateTime());
    const [sampleEventPhaseId, setSampleEventPhaseId] = useState('');
    const [confirmedDuplicateSampleEvent, setConfirmedDuplicateSampleEvent] = useState(null);
    const [procedureId, setProcedureId] = useState('');
    const [otherPurpose, setOtherPurpose] = useState('');
    const [compassDeclination, setCompassDeclination] = useState('');
    const [notes, setNotes] = useState('');
    const [safetyNotes, setSafetyNotes] = useState('');
    const [otherStaffMembers, setOtherStaffMembers] = useState('');
    const [isPerformanceMonitoring, setIsPerformanceMonitoring] = useState('');
    const [crewMembers, setCrewMembers] = useState([]);
    const [hasChanges, setHasChanges] = useState(false);
    const [initialized, setInitialized] = useState(false);
    
    const phaseTitle = sampleEventPhaseTypes.find(phase => String(phase.sampleEventPhaseId) === String(sampleEventPhaseId))?.name;
    const procedureTitle = procedures.find(procedure => String(procedure.procedureId) === String(procedureId))?.name;

    useEffect(() => {
        if (existingSampleEvent) {
            setHierarchyId(existingSampleEvent.hierarchyId);
            setSampleEventId(existingSampleEvent.sampleEventId);
            setStartDate(existingSampleEvent.startDate ?? '');
            setSampleEventPhaseId(existingSampleEvent.sampleEventPhaseId ?? 1);
            setProcedureId(existingSampleEvent.procedureId ?? 1);
            setOtherPurpose(existingSampleEvent.otherPurpose ?? '');
            setCompassDeclination(existingSampleEvent.compassDeclination ?? project.compassDeclination ?? '');
            setNotes(existingSampleEvent.notes ?? '');
            setSafetyNotes(existingSampleEvent.safetyNotes ?? '');
            setCrewMembers(existingSampleEvent.SampleEventStaff ?? []);
            setOtherStaffMembers(existingSampleEvent.otherStaffMembers ?? '');
            setIsPerformanceMonitoring(existingSampleEvent.isPerformanceMonitoring ?? 0);
            setConfirmedDuplicateSampleEvent(`${existingSampleEvent.sampleEventPhaseId}-${existingSampleEvent.procedureId}`)
        } else {
            setHierarchyId(nextId());
            setSampleEventId(nextId());
        }
    }, []);

    useUpdateEffect(() => {
        if (initialized) {
            setHasChanges(true);
        }

        // Ignore first iteration because data is being set by other useEffect
        setInitialized(true);
    }, [startDate, sampleEventPhaseId, procedureId, otherPurpose, compassDeclination, notes, safetyNotes, crewMembers, otherStaffMembers]);
    
    const projectPhaseHierarchyId = `${project?.hierarchyId}/${sampleEventPhaseId}`;
    
    const updatedSampleEvent = {
        ...existingSampleEvent,
        hierarchyId,
        hierarchyTypeId: HIERARCHY_TYPE_SAMPLE_EVENT,
        sampleEventId,
        programContextNodeId: project?.programContextNodeId,
        projectContextNodeId: project?.contextNodeId,
        parentHierarchyId: projectPhaseHierarchyId,
        projectPhaseHierarchyId,
        projectId: project?.projectId,
        startDate,
        sampleEventTypeId: 1,
        sampleEventPhaseId: Number(sampleEventPhaseId),
        procedureId: Number(procedureId),
        otherPurpose,
        compassDeclination,
        notes,
        safetyNotes,
        otherStaffMembers,
        isPerformanceMonitoring,
        SampleEventStaff: crewMembers,
        bytes: existingSampleEvent?.bytes ?? 0,
    };

    const save = () => {
        dispatch({type: UPSERT_SAMPLE_EVENT, sampleEvent: updatedSampleEvent});
        
        if (!existingSampleEvent) {
            copyEventData(updatedSampleEvent);

            if (autoUpload) {
                dispatch({type: SET_AUTO_UPLOAD_ID, autoUploadId: updatedSampleEvent.hierarchyId});
            }
        }

        setHasChanges(false);
        history.goBack();
    };

    const validateSampleEventPhaseId = () => {
        if (
            (!existingSampleEvent || String(existingSampleEvent?.sampleEventId).startsWith(TEMP_ID_PREFIX)) ||
            Number(sampleEventPhaseId) === Number(existingSampleEvent?.sampleEventPhaseId)
        ) {
            return null;
        }
        return "Cannot be modified after event has been uploaded.";
    }

    const disableSave = () => {
        const requiredMembers = otherStaffMembers ? [...crewMembers, otherStaffMembers] : crewMembers;
        const requiredFields = [startDate, sampleEventPhaseId, compassDeclination, isPerformanceMonitoring, requiredMembers, procedureId];
        const validatedFields = [
            validateSampleEventPhaseId(),
            validateBetween(compassDeclination, 0, 360),
            validateInteger(compassDeclination),
        ];

        return hasInvalidField(requiredFields, validatedFields);
    };

    return (
        <Fragment>
            <RouteLeavingGuard when={hasChanges} />
            <ConfirmSaveModal
                show={
                    confirmedDuplicateSampleEvent !== `${sampleEventPhaseId}-${procedureId}`
                    && (existingSampleEventPhaseIds?.[sampleEventPhaseId]?.[procedureId]?.some?.(sampleEventId => String(sampleEventId) !== String(existingSampleEvent?.sampleEventId)) ?? false)
                }
                size="md"
                title={`Confirm Duplicate Event Phase`}
                message={<p>
                    A {phaseTitle} {procedureTitle} monitoring event already exists for this project.<br/>
                    Are you sure you want to create a new one?
                </p>}
                saveButtonTitle="I'm Sure"
                onSave={() => {
                    setConfirmedDuplicateSampleEvent(`${sampleEventPhaseId}-${procedureId}`);
                }}
                hideButtonTitle="Pick a Different Phase"
                onHide={() => {
                    setSampleEventPhaseId('');
                }}
            />
            <PageContainer className="new-event-page">
                <PageHeader>
                    <Title title={existingSampleEvent ? `Monitoring Event - ${existingSampleEvent.fullName}` : 'New Monitoring Event'} />
                </PageHeader>
                <PageContent>
                    <Row xs={12}>
                        <Col xs={6}>
                            <Row xs={12}>
                                <Col xs={6}>
                                    <FormInput
                                        label="Phase"
                                        type="select"
                                        primaryKey="sampleEventPhaseId"
                                        placeholder="Select Phase"
                                        options={sampleEventPhaseTypes}
                                        value={sampleEventPhaseId}
                                        onChange={value => setSampleEventPhaseId(value)}
                                        validationMessage={validateSampleEventPhaseId()}
                                        required
                                    />
                                </Col>
                                <Col xs={6}>
                                    <FormInput
                                        label="Procedure"
                                        type="select"
                                        primaryKey="procedureId"
                                        placeholder="Select Procedure"
                                        options={procedures}
                                        value={procedureId}
                                        onChange={value => setProcedureId(value)}
                                        disabled={!!existingSampleEvent}
                                        required
                                    />
                                </Col>
                                <Col xs={6}>
                                    <FormInput
                                        label="Purpose"
                                        overlayIcon={
                                            <OverlayTrigger
                                                trigger="click"
                                                key="right"
                                                placement="right"
                                                overlay={
                                                    <Popover>
                                                        <Popover.Content>
                                                            Only use this field when you need to describe an atypical purpose of monitoring, such as "Academic Research Project for Intern".
                                                        </Popover.Content>
                                                    </Popover>
                                                }
                                                rootClose
                                            >
                                                <FontAwesomeIcon icon={['fas', 'info-circle']} className="info-popover" />
                                            </OverlayTrigger>
                                        }
                                        value={otherPurpose}
                                        onChange={value => setOtherPurpose(value)}
                                        placeholder="Research"
                                    />
                                </Col>
                                <Col xs={6}>
                                    <FormInput
                                        label="Start Date"
                                        type="datetime-local"
                                        value={DateUtils.FormatToLocalDateTime(startDate)}
                                        onChange={value => setStartDate(DateUtils.FormatToUTCDateTime(value))}
                                        required
                                    />
                                </Col>
                                <Col xs={6}>
                                    <FormInput
                                        label="Compass Declination"
                                        type="text"
                                        inputmode="decimal"
                                        pattern="\d+"
                                        value={compassDeclination}
                                        onChange={value => setCompassDeclination(value)}
                                        validationMessage={
                                            validateBetween(compassDeclination, 0, 360) ||
                                            validateInteger(compassDeclination)
                                        }
                                        suffix="&#176;E"
                                        required
                                    />
                                </Col>
                                <Col xs={6}>
                                    <FormInput
                                        label="Performance Monitoring"
                                        type="select"
                                        options={[
                                            { id: 1, name: 'Yes' },
                                            { id: 0, name: 'No' },
                                        ]}
                                        value={isPerformanceMonitoring}
                                        onChange={value => setIsPerformanceMonitoring(value)}
                                        overlayIcon={
                                            <OverlayTrigger
                                                trigger="click"
                                                key="right"
                                                placement="right"
                                                overlay={
                                                    <Popover>
                                                        <Popover.Content>
                                                            Choose 'Yes' if this monitoring event is collecting performance data for reporting purposes. Choose 'No' if the event is another type of site visit such assessing maintenance needs.
                                                        </Popover.Content>
                                                    </Popover>
                                                }
                                                rootClose
                                            >
                                                <FontAwesomeIcon icon={['fas', 'info-circle']} className="info-popover" />
                                            </OverlayTrigger>
                                        }
                                        required
                                    />
                                </Col>
                                <Col xs={12}>
                                    <FormInput
                                        label="Notes"
                                        type="textarea"
                                        value={notes}
                                        onChange={value => setNotes(value)}
                                    />
                                </Col>
                                <Col xs={12}>
                                    <FormInput
                                        label="Safety Notes"
                                        type="textarea"
                                        value={safetyNotes}
                                        onChange={value => setSafetyNotes(value)}
                                    />
                                </Col>
                            </Row>
                        </Col>
                        <Col xs={6}>
                            <CheckboxList
                                label={
                                    <Fragment>
                                        <span>Field Crew</span>
                                        <OverlayTrigger
                                            trigger="click"
                                            key="bottom"
                                            placement="bottom"
                                            overlay={
                                                <Popover>
                                                    <Popover.Content>
                                                        Program and Project Managers can add Users to this list by assigning the User to this Project in StreamBank Admin.
                                                    </Popover.Content>
                                                </Popover>
                                            }
                                            rootClose
                                        >
                                            <FontAwesomeIcon icon={['fas', 'info-circle']} className="info-popover" />
                                        </OverlayTrigger>
                                    </Fragment>
                                }
                                data={users}
                                primaryKey="userId"
                                labelKey="name"
                                checked={crewMembers}
                                onChange={(value) => setCrewMembers(value)}
                                required
                            />
                            <FormInput
                                label="Other Staff"
                                value={otherStaffMembers}
                                onChange={value => setOtherStaffMembers(value)}
                            />
                        </Col>
                    </Row>
                </PageContent>
                <PageFooter>
                    <SaveButtonWithHelpIcon
                        type="Event"
                        hasData={!!existingSampleEvent}
                        save={save}
                        disable={disableSave()}
                    />
                    <Button variant="link" className="ml-3" onClick={() => history.goBack()}>
                        <FontAwesomeIcon icon={['fal', 'times']} />
                        Cancel
                    </Button>
                </PageFooter>
            </PageContainer>
        </Fragment>
    );
};

export default NewEventPage;
